/**
 * The form question bank, as the Forms settings page and the form builder
 * both read it: the tenant's questions with their response set's answers
 * filled in, the answer types a form can ask, and which questions can score
 * or fail.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  useEntityTypeByKey,
  type EntityAttributeDefinition,
  type EntityFieldType,
} from "@/admin/hooks/use-entities";
import { useFormResponseSets, type FormResponseSet } from "@/admin/hooks/use-forms";

/** The tenant entity whose fields are the form questions. */
export const FORM_ENTITY_KEY = "form_request";

export type Question = EntityAttributeDefinition;
export type Option = { value: string; label: string };

/** What a pick-one question on a response set can score or fail on. */
export type QuestionScoring = { scored: boolean; canFail: boolean };

/** A response set's answers as question options; saved answers always have a value. */
export function setOptions(set: FormResponseSet): Option[] {
  return set.options.map((answer) => ({ value: answer.value ?? "", label: answer.label }));
}

export function optionsOf(question: Question | undefined): Option[] {
  return (question?.configuration_data?.options ?? []) as Option[];
}

export function typeLabel(types: Option[], fieldType: string): string {
  return (
    types.find((type) => type.value === fieldType)?.label ??
    fieldType.replaceAll("_", " ").toLowerCase()
  );
}

export function useFormQuestionBank() {
  const { t } = useTranslation();
  const entityQuery = useEntityTypeByKey(FORM_ENTITY_KEY);
  const entity = entityQuery.data;
  const setsQuery = useFormResponseSets();
  const responseSets = useMemo(() => setsQuery.data ?? [], [setsQuery.data]);

  // A question on a response set asks that set's answers.
  const questions = useMemo(() => {
    const answers = new Map(responseSets.map((s) => [s.key, setOptions(s)]));
    return (entity?.attributes ?? [])
      .filter((attr) => attr.source === "EAV")
      .map((attr): Question => {
        const set = attr.configuration_data?.response_set as string | undefined;
        return set && answers.has(set)
          ? {
              ...attr,
              configuration_data: { ...attr.configuration_data, options: answers.get(set) },
            }
          : attr;
      });
  }, [entity, responseSets]);

  // Pick-one questions whose response set's answers carry a score or a fail.
  const scoring = useMemo(() => {
    const sets = new Map(responseSets.map((s) => [s.key, s.options]));
    const out = new Map<string, QuestionScoring>();
    for (const q of questions) {
      const answers = sets.get(String(q.configuration_data?.response_set ?? ""));
      if (q.field_type !== "SELECT" || !answers) continue;
      const scored = answers.some((a) => typeof a.score === "number");
      const canFail = answers.some((a) => a.outcome === "fail");
      if (scored || canFail) out.set(q.code_name, { scored, canFail });
    }
    return out;
  }, [questions, responseSets]);

  // What the recipient's form can render; money, address and record search need lookups it lacks.
  const answerTypes = useMemo<{ value: EntityFieldType; label: string }[]>(
    () => [
      { value: "TEXT", label: t("forms.settings.typeText", "Short text") },
      { value: "RICH_TEXT", label: t("forms.settings.typeLongText", "Long text") },
      { value: "NUMBER", label: t("forms.settings.typeNumber", "Number") },
      { value: "SELECT", label: t("forms.settings.typePickOne", "Pick one") },
      { value: "MULTI_SELECT", label: t("forms.settings.typePickSeveral", "Pick several") },
      { value: "BOOLEAN", label: t("forms.settings.typeTickBox", "Tick box") },
      { value: "DATE", label: t("forms.settings.typeDate", "Date") },
      { value: "DATE_TIME", label: t("forms.settings.typeDateTime", "Date and time") },
      { value: "EMAIL", label: t("forms.settings.typeEmail", "Email") },
      { value: "PHONE", label: t("forms.settings.typePhone", "Phone") },
      { value: "LINK", label: t("forms.settings.typeLink", "Link") },
      { value: "IMAGE", label: t("forms.settings.typePhotos", "Photos") },
    ],
    [t],
  );

  return { entityQuery, entity, setsQuery, responseSets, questions, scoring, answerTypes };
}
